import React from "react";
import { ChangeEvent, FormEvent } from "react";

import { DateTime } from "luxon";

import { V1, V2 } from "../../Api";
// import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, formatTimeLocal } from "../../Utilities";
import withTitle from "../withTitle";

import ValidateInput from "../ValidateInput";
import { WellKnownGroups } from "../AuthContext";
import { formatName } from "../../Utilities";
import Checkbox from "../Checkbox";

interface ISurgeryBookCreationViewState {
    isLoading: boolean | string;

    after: DateTime;
    before: DateTime;

    start: string,
    end: string,

    month: string;
    reportType: string;

    locations: any[];
    surgeryLocation: string;
    person: string;
    //personType: "all" | "provider" | "marketer" | "surgeon" | "anesthetist";
    visitType: string;

    marketers: any[];
    marketerExtra: any[];
    nurses: any[];
    anesthetists: any[];

    timeMode: "month" | "start_end";

    hidePatientName: boolean;

    [name: string]: any;
}

class SurgeryBookCreationView extends React.Component<{}, ISurgeryBookCreationViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<{}>) {
        super(props);

        const today = DateTime.local();

        this.state = {
            isLoading: true,

            // These are computed before use
            after: DateTime.now(),
            before: DateTime.now(),

            start: "",
            end: "",

            month: today.toFormat("yyyy-MM"),
            reportType: "!office",

            locations: [],
            surgeryLocation: "",
            person: "all",
            //personType: "all",
            visitType: "",

            marketers: [],
            marketerExtra: [],
            nurses: [],
            anesthetists: [],

            timeMode: "month",

            hidePatientName: false,
        };

        this.openTableWindow = this.openTableWindow.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.handleMessage = this.handleMessage.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        try {
            const [locations, marketers, marketerExtra, nurses, anesthetists] = await Promise.all([
                V2.get<any[]>("locations?type=surgery"),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.MarketerExtra}`),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`),
                V1.get<any[]>(`employees/summary?group=${WellKnownGroups.ORAnesthistist}`),
            ]);
            locations.unshift({ id: "", name: "" });

            this.setState({
                isLoading: false,
                locations,
                marketers: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                marketerExtra: marketerExtra.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                nurses: nurses.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                anesthetists: anesthetists.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage);
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="spinner-center lds-dual-ring-big" />;
        }
        else if (typeof this.state.isLoading === "string") {
            return <span className="error-text">{this.state.isLoading}</span>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Report Parameters</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-location" className="required">Surgery Center Location</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-location" name="surgeryLocation" value={this.state.surgeryLocation} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName ? `${a.shortName} - ` : ""}{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-type" className="required">Type</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-type" name="reportType" value={this.state.reportType} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                        <option value="!office">OR & Indigent cases</option>
                                        <option value="officeindigent">Indigent cases</option>
                                        <option value="officeindigent_year">Indigent cases, whole year</option>
                                        <option value="office">Office cases</option>
                                    </ValidateInput>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-hide-hide-patient-name">&nbsp;</label>
                                    <div className="input-form-group-inline">
                                        <Checkbox fancy={true} type="checkbox" id="frm-hide-hide-patient-name" name="hidePatientName" checked={this.state.hidePatientName} onChange={this.formInputChanged} autoComplete="off_please_chrome">Hide Patient Name</Checkbox>
                                    </div>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-person">Filter by person</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-person" name="person" value={this.state.person} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                        <option value="all/">Everyone</option>
                                        <optgroup label="Marketers">
                                            <option value="no_marketer">No Marketer</option>
                                            {this.state.marketers.map(a => <option key={`marketer/${a.id}`} value={`marketer/${a.id}`}>{formatName(a.name)}</option>)}
                                        </optgroup>
                                        {
                                            this.state.marketerExtra.length > 0
                                                ? <optgroup label="Others">
                                                    {this.state.marketerExtra.map(a => <option key={`marketer/${a.id}`} value={`marketer/${a.id}`}>{formatName(a.name)}</option>)}
                                                </optgroup>
                                                : null
                                        }
                                        <optgroup label="Providers">
                                            <option value="no_provider">No Provider</option>
                                            {this.state.nurses.map(a => <option key={`provider/${a.id}`} value={`provider/${a.id}`}>{formatName(a.name)}</option>)}
                                        </optgroup>
                                        <optgroup label="Anesthetist">
                                            <option value="no_anesthetist">No Anesthetist</option>
                                            {this.state.anesthetists.map(a => <option key={`anesthetist/${a.id}`} value={`anesthetist/${a.id}`}>{formatName(a.name)}</option>)}
                                        </optgroup>
                                    </ValidateInput>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-visit-type">Filter by visit type</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-visit-type" name="visitType" value={this.state.visitType} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                        <option value="">All Visit Types</option>
                                        <option value="physical">Physical Visit</option>
                                        <option value="telemed">Telemedicine Visit</option>
                                    </ValidateInput>
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Date/Time Parameters</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-time-mode">Date/Time Mode</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-time-mode" name="timeMode" value={this.state.timeMode} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                        <option value="month">By Month</option>
                                        <option value="start_end">Start & End</option>
                                    </ValidateInput>
                                </div>
                            </section>
                            {this.state.timeMode === "month"
                                ? <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-month" className="required">Month</label>
                                        <ValidateInput type="month" ref={this.receiveValidationRef} id="frm-month" name="month" value={this.state.month} disabled={this.state.reportType === "officeindigent_year"} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                                : <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-start" className="required">Start</label>
                                        <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-start" name="start" value={this.state.start} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-end" className="required">End (Inclusive)</label>
                                        <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-end" name="end" value={this.state.end} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                            }
                        </section>
                    </div>
                    &nbsp;
                    <div className="action-panel">
                        <button type="submit" className="button push-right" disabled={this.state.submitStatus === true}>Run Report</button>
                    </div>
                </form>
            </div>
        );
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    private openTableWindow() {
        window.addEventListener("message", this.handleMessage, false);

        window.open("/print/surgery/book", "BOOK_WINDOW", "resizable,scrollbars");
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: (state: Partial<ISurgeryBookCreationViewState>, prevState: ISurgeryBookCreationViewState) => void) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            const newState = {
                hasChanged: true,
                [name]: value
            }

            if (callback) {
                const ns: any = {};
                Object.assign(ns, s);
                Object.assign(ns, newState);

                callback(newState, ns);
            }

            return newState;
        });
    }

    private submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        // Calculate before sending to new window
        let after;
        let before;

        switch (this.state.timeMode) {
            case "month":
                after = DateTime.fromISO(this.state.month).startOf("month");
                before = DateTime.fromISO(this.state.month).endOf("month").plus({ millisecond: 1 });
                break;
            case "start_end":
                after = DateTime.fromISO(this.state.start);
                before = DateTime.fromISO(this.state.end).endOf("day").plus({ millisecond: 1 });
                break;
        }

        this.setState({
            after,
            before
        }, () => {
            this.openTableWindow();
        });
    }

    private handleMessage(e: MessageEvent) {
        if (!e.source || (e.source instanceof MessagePort) || (e.source instanceof ServiceWorker)) {
            return;
        }

        if (e.data.action && e.data.action === "report_request") {
            // Popup is asking for parameters

            let [personType, person] = this.state.person.split("/", 2);

            if (person === "no_marketer") {
                person = "-1";
                personType = "marketer";
            }
            else if (person === "no_provider") {
                person = "-1";
                personType = "provider";
            }
            else if (person === "no_anesthetist") {
                person = "-1";
                personType = "anesthetist";
            }

            e.source.postMessage({
                action: "report_parameters",
                after: this.state.after.toISO(),
                before: this.state.before.toISO(),
                location: this.state.surgeryLocation,
                reportType: this.state.reportType,
                person: person,
                personType: personType,
                visitType: this.state.visitType,
                hidePatientName: this.state.hidePatientName,
            }, "*");
        }
    }
}

export default withTitle("Surgery Report - Book", SurgeryBookCreationView);
